import { useContext, useEffect, useState } from 'react'
import useFetch, { CachePolicies } from 'use-http'
import type { ListAllActiveUsersResponse } from '../interfaces'
import { UserContext } from '../providers'

// Module-level cache keyed by clientNumber to invalidate on account switch
const cacheByClient = new Map<
  string,
  { users: ListAllActiveUsersResponse[]; emailToNameMap: Map<string, string> }
>()
let fetchInProgress: string | null = null

interface UseActiveUsersResult {
  users: ListAllActiveUsersResponse[]
  emailToNameMap: Map<string, string>
  loading: boolean
}

export const useActiveUsers = (): UseActiveUsersResult => {
  const { user } = useContext(UserContext)
  const clientNumber = user?.clientNumber ?? ''
  const cached = clientNumber ? cacheByClient.get(clientNumber) : undefined

  const [users, setUsers] = useState<ListAllActiveUsersResponse[]>(
    cached?.users ?? [],
  )
  const [emailToNameMap, setEmailToNameMap] = useState<Map<string, string>>(
    cached?.emailToNameMap ?? new Map(),
  )

  const { request, loading, abort } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  useEffect(() => {
    if (!clientNumber) return

    const existing = cacheByClient.get(clientNumber)
    if (existing) {
      setUsers(existing.users)
      setEmailToNameMap(existing.emailToNameMap)
      return
    }

    // Prevent duplicate concurrent fetches for the same clientNumber
    if (fetchInProgress === clientNumber) return

    const fetchUsers = async () => {
      fetchInProgress = clientNumber
      try {
        const response: ListAllActiveUsersResponse[] = await request.get(
          `/api/clients/${clientNumber}/users/active`,
        )
        if (Array.isArray(response)) {
          const map = new Map<string, string>()
          response.forEach(u => map.set(u.userEmail, u.userName))
          cacheByClient.set(clientNumber, { users: response, emailToNameMap: map })
          setUsers(response)
          setEmailToNameMap(map)
        }
      } finally {
        if (fetchInProgress === clientNumber) {
          fetchInProgress = null
        }
      }
    }

    fetchUsers()

    return () => {
      abort()
    }
  }, [clientNumber])

  return { users, emailToNameMap, loading }
}
